import {
  ActionIcon,
  Button,
  CloseButton,
  Group,
  Loader,
  Menu,
  Popover,
  Tabs,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { keepPreviousData, useInfiniteQuery, useQueries } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { ArrowUpDown, Check, Search, Type } from "lucide-react";
import { useCallback, useId, useState } from "react";

import { getGetAssetQueryOptions } from "../../../api/generated/endpoints/assets/assets";
import {
  getListPromptTemplatesQueryKey,
  listPromptTemplates,
} from "../../../api/generated/endpoints/generation/generation";
import type {
  AssetOrder,
  ListPromptTemplatesParams,
  PromptTemplate,
  PromptTemplateKind,
} from "../../../api/generated/model";
import { pictureOf, type PickedImage } from "../../../platform/media/ImagePicker";
import { EmptyState } from "../../../ui/EmptyState";
import { LoadAhead, morePages } from "../../../ui/LoadAhead";
import { ROOM_ABOVE } from "../../../ui/popover-room";
import {
  outlinedTemplate,
  templatePictures,
  usablePicture,
  type TemplateDraft,
} from "./prompt-templates";
import classes from "./prompt-templates.module.css";

const ROUTE = "/signed-in/teams/$teamId/brands/$brandId";
/** Live's page (`PROMPT_TEMPLATES_PAGE_SIZE`) and its search's wait (`useDebouncedValue`). */
const PAGE = 24;
const SEARCH_WAIT_MS = 500;
/** The route's bound on a search. */
const SEARCH_MAX = 255;

/** Live's sort (`constants/filters.ts`): newest first by default. */
const ORDERS: { value: AssetOrder; label: string }[] = [
  { value: "newest", label: "Recently Added" },
  { value: "oldest", label: "Oldest First" },
];

export interface PromptTemplatesProps {
  /** The draft the outline compares with. */
  draft: TemplateDraft;
  /** Live's Apply: the template's prompt, the pictures that still load, and which template it is. */
  onApply: (prompt: string, images: PickedImage[], templateId: string) => void;
  /** The preview's button: live's "Apply Prompt" in the composer; a page names where it goes. */
  applyLabel?: string;
}

/**
 * Live's "Prompt templates" tool in the simple image composer (spec G12; captures gaps 12 to 16):
 * the "T" icon opens live's panel above the composer, "Prompt Templates" with the brand's own
 * (Custom, first) and live's global ones (Global), one search and one sort for both tabs, a grid
 * of covers 24 at a time; a cover opens its prompt and "Apply Prompt", which closes the panel.
 */
export function PromptTemplates({ draft, onApply }: PromptTemplatesProps) {
  const [opened, setOpened] = useState(false);
  const titleId = useId();
  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="top-start"
      offset={8}
      middlewares={ROOM_ABOVE}
      trapFocus
      returnFocus
    >
      <Tooltip label="Prompt templates" withinPortal={false} disabled={opened}>
        <Popover.Target>
          <ActionIcon
            variant={opened ? "filled-light" : "subtle"}
            size={36}
            aria-label="Prompt templates"
            aria-expanded={opened}
            onClick={() => setOpened((open) => !open)}
          >
            <Type size={20} strokeWidth={1.5} />
          </ActionIcon>
        </Popover.Target>
      </Tooltip>
      <Popover.Dropdown className={classes.dropdown} aria-labelledby={titleId}>
        {/* Mounted while open: each opening starts on Custom with no search, as live's. */}
        <Panel
          titleId={titleId}
          draft={draft}
          onClose={() => setOpened(false)}
          onApply={(prompt, images, templateId) => {
            onApply(prompt, images, templateId);
            setOpened(false);
          }}
        />
      </Popover.Dropdown>
    </Popover>
  );
}

interface PanelProps extends PromptTemplatesProps {
  titleId: string;
  onClose: () => void;
}

function Panel({ titleId, draft, onClose, onApply }: PanelProps) {
  return (
    <div className={classes.panel}>
      <div className={classes.header}>
        <Text id={titleId} className={classes.title}>
          Prompt Templates
        </Text>
        <CloseButton aria-label="Close" onClick={onClose} />
      </div>
      <PromptTemplateBrowser draft={draft} onApply={onApply} />
    </div>
  );
}

/**
 * The panel's body: Custom and Global, one search and one sort for both, the grid of covers and
 * each one's prompt with its button. The composer's panel draws it; so does Pro's Library tab
 * (spec ui-perf/pro-library-tabs), in a box of its own height, as its grid scrolls within it.
 */
export function PromptTemplateBrowser({ draft, onApply, applyLabel = "Apply Prompt" }: PromptTemplatesProps) {
  const [kind, setKind] = useState<PromptTemplateKind>("custom");
  const [typed, setTyped] = useState("");
  const [search] = useDebouncedValue(typed.trim(), SEARCH_WAIT_MS);
  const [order, setOrder] = useState<AssetOrder>("newest");
  const grid = (each: PromptTemplateKind) => (
    <TemplateGrid
      kind={each}
      search={search}
      order={order}
      draft={draft}
      onApply={onApply}
      applyLabel={applyLabel}
    />
  );
  return (
    <Tabs
      className={classes.tabs}
      value={kind}
      onChange={(next) => setKind(next === "global" ? "global" : "custom")}
      keepMounted={false}
    >
      <Tabs.List>
        <Tabs.Tab value="custom">Custom</Tabs.Tab>
        <Tabs.Tab value="global">Global</Tabs.Tab>
      </Tabs.List>
      <div className={classes.toolbar}>
        <TextInput
          className={classes.search}
          aria-label="Search"
          placeholder="Search"
          leftSection={<Search size={16} aria-hidden />}
          value={typed}
          maxLength={SEARCH_MAX}
          onChange={(event) => setTyped(event.currentTarget.value)}
        />
        <Menu position="bottom-end" withinPortal={false}>
          <Menu.Target>
            <ActionIcon variant="subtle" size="lg" aria-label="Sort">
              <ArrowUpDown size={20} strokeWidth={1.5} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            {ORDERS.map((each) => (
              <Menu.Item
                key={each.value}
                rightSection={each.value === order ? <Check size={16} /> : null}
                onClick={() => setOrder(each.value)}
              >
                {each.label}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      </div>
      <Tabs.Panel value="custom" className={classes.scroll}>
        {grid("custom")}
      </Tabs.Panel>
      <Tabs.Panel value="global" className={classes.scroll}>
        {grid("global")}
      </Tabs.Panel>
    </Tabs>
  );
}

interface TemplateGridProps {
  kind: PromptTemplateKind;
  search: string;
  order: AssetOrder;
  draft: TemplateDraft;
  onApply: (prompt: string, images: PickedImage[], templateId: string) => void;
  applyLabel: string;
}

function TemplateGrid({ kind, search, order, draft, onApply, applyLabel }: TemplateGridProps) {
  const { teamId, brandId } = useParams({ from: ROUTE });
  // The tile whose prompt shows: it takes the outline while it does, as live's.
  const [previewed, setPreviewed] = useState<string | null>(null);
  const params: ListPromptTemplatesParams = {
    kind,
    ...(order === "newest" ? {} : { order }),
    ...(search === "" ? {} : { search }),
  };
  const templates = useInfiniteQuery({
    queryKey: [...getListPromptTemplatesQueryKey(teamId, brandId, params), "pages"],
    queryFn: ({ pageParam, signal }) =>
      listPromptTemplates(
        teamId,
        brandId,
        { ...params, limit: PAGE, ...(pageParam === undefined ? {} : { after: pageParam }) },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
    // A new search keeps the tiles shown until its own come.
    placeholderData: keepPreviousData,
  });
  const items = templates.data?.pages.flatMap((page) => page.items) ?? [];
  const outlined = previewed ?? outlinedTemplate(items, draft)?.id ?? null;
  // The tab's own panel scrolls the grid: its end is watched there, not in the window, which the
  // panel may run past (Pro's Library tab on a phone, spec ui-perf/pro-library-tabs-375).
  const [root, setRoot] = useState<HTMLElement | null>(null);
  const attach = useCallback(
    (grid: HTMLUListElement | null) => setRoot(grid?.closest<HTMLElement>('[role="tabpanel"]') ?? null),
    [],
  );
  if (templates.isPending) return <Loader size="sm" aria-label="Loading the templates" />;
  // Live shows its empty words when the list fails too.
  if (items.length === 0) {
    return (
      <EmptyState
        title="No results found"
        description="Check for typos or filters, or try a different search query."
      />
    );
  }
  return (
    <>
      <ul
        ref={attach}
        className={classes.grid}
        aria-label={kind === "custom" ? "Custom templates" : "Global templates"}
      >
        {items.map((template) => (
          <li key={template.id}>
            <TemplateTile
              template={template}
              outlined={template.id === outlined}
              open={template.id === previewed}
              onOpen={() => setPreviewed(template.id)}
              onClose={() => setPreviewed(null)}
              onApply={onApply}
              applyLabel={applyLabel}
            />
          </li>
        ))}
      </ul>
      {/* Live's: the next page as the end shows (`InfiniteScroll`), no button. */}
      <LoadAhead
        more={morePages(templates)}
        loaded={items.length}
        root={root}
        label="Loading more templates"
      />
    </>
  );
}

interface TemplateTileProps {
  template: PromptTemplate;
  outlined: boolean;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onApply: (prompt: string, images: PickedImage[], templateId: string) => void;
  applyLabel: string;
}

/** A cover, and live's popover with its prompt beside it (captures 14, 16). */
function TemplateTile({ template, outlined, open, onOpen, onClose, onApply, applyLabel }: TemplateTileProps) {
  return (
    <Popover
      opened={open}
      onChange={(next) => (next ? onOpen() : onClose())}
      position="right-start"
      offset={8}
      // Inside the panel's dropdown, so a click in it is not outside the panel.
      withinPortal={false}
      trapFocus
      returnFocus
    >
      <Popover.Target>
        <UnstyledButton
          className={classes.tile}
          data-outlined={outlined || undefined}
          aria-label={template.name}
          aria-expanded={open}
          onClick={open ? onClose : onOpen}
        >
          {template.cover_url === null ? (
            <span className={classes.placeholder} aria-hidden>
              <Type size={28} strokeWidth={1.5} />
            </span>
          ) : (
            <img src={template.cover_url} alt="" className={classes.cover} />
          )}
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown className={classes.preview} aria-label={template.name}>
        {/* Mounted while open: its references are read when it shows. */}
        <Preview template={template} onApply={onApply} applyLabel={applyLabel} />
      </Popover.Dropdown>
    </Popover>
  );
}

/**
 * Live's popover: "Prompt", the prompt, its reference images when it has some, and "Apply
 * Prompt". The references are read as the composer reads a picked file; one gone from the
 * library since the save is not applied (G12 decision 8).
 */
function Preview(props: {
  template: PromptTemplate;
  onApply: (prompt: string, images: PickedImage[], templateId: string) => void;
  applyLabel: string;
}) {
  const { template, onApply, applyLabel } = props;
  const { teamId, brandId } = useParams({ from: ROUTE });
  const reads = useQueries({
    queries: templatePictures(template).map((assetId) =>
      getGetAssetQueryOptions(teamId, brandId, assetId, { query: { retry: false } }),
    ),
  });
  const pictures = reads.flatMap((read) =>
    read.data !== undefined && usablePicture(read.data)
      ? [{ assetId: read.data.id, url: pictureOf(read.data), name: read.data.name }]
      : [],
  );
  const reading = reads.some((read) => read.isPending);
  return (
    <div className={classes.previewBody}>
      <Text className={classes.label}>Prompt</Text>
      <Text className={classes.prompt}>{template.prompt}</Text>
      {reads.length === 0 ? null : (
        <>
          <Text className={classes.label}>Reference images</Text>
          {reading ? <Loader size="xs" aria-label="Loading the reference images" /> : null}
          <div className={classes.references}>
            {pictures.map((picture) =>
              picture.url === null ? (
                <span key={picture.assetId} className={classes.reference} aria-hidden />
              ) : (
                <img
                  key={picture.assetId}
                  src={picture.url}
                  alt={picture.name}
                  className={classes.reference}
                />
              ),
            )}
          </div>
        </>
      )}
      <Group justify="center">
        <Button loading={reading} onClick={() => onApply(template.prompt, pictures, template.id)}>
          {applyLabel}
        </Button>
      </Group>
    </div>
  );
}
